// Header NFR: menu laterale, ricerca predittiva e carrello a cassetto (porting del vecchio sito Next).
const ROOT = window.Shopify?.routes?.root || '/';

// Ricerca per categoria: parole dei clienti → termine del catalogo e tipo prodotto da mettere in cima.
// La ricerca di Shopify è "semantica" e spinge in fondo gli esauriti: senza questo, cercando
// "pantaloni" uscivano solo felpe.
const SEARCH_CATEGORIES = [
  { re: /^(pantalon[ei]|pants?|tut[ae]|jogger?s?|sweatpants?|track ?pants?)$/i, term: 'sweatpants', type: /sweatpants/i },
  { re: /^(t-?shirts?|tee|tees|maglietta|magliette|maglia|maglie)$/i, term: 't-shirt', type: /t ?-?shirt/i },
  { re: /^(felp[ae]|hoodies?|cappuccio)$/i, term: 'felpa', type: /felpa cappuccio/i },
  { re: /^(girocollo|crewnecks?|sweatshirts?)$/i, term: 'crewneck', type: /girocollo/i },
  { re: /^(zip|zip ?hoodies?|felp[ae] (con )?zip)$/i, term: 'zip hoodie', type: /zip/i },
  { re: /^(camici[ae]|shirts?)$/i, term: 'shirt', type: /camici/i },
  { re: /^(calz(ini|e)|socks?)$/i, term: 'calzini', type: /calzini/i },
];
const RECENT_KEY = 'nfr_recent_searches';

function formatMoney(cents, format) {
  const value = (Math.round(cents) / 100).toFixed(2);
  const [int, dec] = value.split('.');
  const dots = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const commas = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return (format || '€{{amount_with_comma_separator}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    if (key === 'amount_with_comma_separator') return `${dots},${dec}`;
    if (key === 'amount_no_decimals_with_comma_separator') return dots;
    if (key === 'amount_no_decimals') return commas;
    return `${commas}.${dec}`;
  });
}

function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function sizedImage(url, width) {
  if (!url) return '';
  return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
}

class NfrHeader extends HTMLElement {
  #abort = new AbortController();
  #searchTimer = null;
  #searchController = null;
  #cart = null;
  #cartSeq = 0;

  // Menu, ricerca e carrello vengono spostati in fondo al <body>: dentro l'header resterebbero
  // sotto gli elementi fissi delle pagine (es. pannello prodotto) per via del contesto di impilamento.
  $(selector) {
    return this.querySelector(selector) || this.layer?.querySelector(selector);
  }

  $$(selector) {
    return [...this.querySelectorAll(selector), ...(this.layer?.querySelectorAll(selector) || [])];
  }

  connectedCallback() {
    const { signal } = this.#abort;
    if (!this.layer) {
      this.layer = document.createElement('div');
      this.layer.className = 'nfr-layer';
      this.layer.append(...this.querySelectorAll('[data-menu-overlay], [data-menu], [data-search], [data-cart-overlay], [data-cart]'));
      document.body.append(this.layer);
    }
    this.fmt = (c) => formatMoney(c, this.dataset.moneyFormat);

    // Altezza header per le pagine che la usano (pagina prodotto a schermo pieno)
    const setHeight = () => document.body.style.setProperty('--header-height', `${this.$('.nfr-nav').offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', setHeight, { signal });

    // Menu
    this.$('[data-open-menu]').addEventListener('click', () => this.#toggleMenu(true), { signal });
    this.$('[data-close-menu]').addEventListener('click', () => this.#toggleMenu(false), { signal });
    this.$('[data-menu-overlay]').addEventListener('click', () => this.#toggleMenu(false), { signal });

    // Ricerca
    this.$('[data-open-search]').addEventListener('click', () => this.#toggleSearch(true), { signal });
    this.$('[data-close-search]').addEventListener('click', () => this.#toggleSearch(false), { signal });
    this.$('[data-search]').addEventListener('click', (e) => {
      if (e.target === e.currentTarget) this.#toggleSearch(false);
    }, { signal });
    const input = this.$('[data-search-input]');
    input.addEventListener('input', () => this.#onSearchInput(), { signal });
    input.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && input.value.trim()) {
        this.#saveRecent(input.value.trim());
        window.location.href = `${ROOT}search?q=${encodeURIComponent(input.value.trim())}&type=product`;
      }
    }, { signal });
    this.$('[data-search-clear]').addEventListener('click', () => {
      input.value = '';
      this.#onSearchInput();
      input.focus();
    }, { signal });
    this.$$('[data-suggest]').forEach((chip) =>
      chip.addEventListener('click', () => {
        input.value = chip.dataset.suggest;
        this.#onSearchInput();
      }, { signal })
    );
    this.$('[data-clear-recent]').addEventListener('click', () => {
      try {
        localStorage.removeItem(RECENT_KEY);
      } catch {}
      this.#renderRecent();
    }, { signal });

    // Carrello
    this.$('[data-open-cart]').addEventListener('click', (e) => {
      e.preventDefault();
      this.openCart();
    }, { signal });
    this.$('[data-close-cart]').addEventListener('click', () => this.#toggleCart(false), { signal });
    this.$('[data-cart-overlay]').addEventListener('click', () => this.#toggleCart(false), { signal });
    this.$('[data-cart-items]').addEventListener('click', (e) => this.#onCartClick(e), { signal });
    const codeInput = this.$('[data-code-input]');
    codeInput.addEventListener('input', () => {
      codeInput.value = codeInput.value.toUpperCase();
      this.$('[data-code-btn]').disabled = !codeInput.value.trim();
    }, { signal });
    this.$('[data-code-form]').addEventListener('submit', (e) => {
      e.preventDefault();
      this.#applyCode(codeInput.value.trim());
    }, { signal });
    this.$('[data-code-remove]').addEventListener('click', () => this.#applyCode(''), { signal });

    document.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      this.#toggleMenu(false);
      this.#toggleSearch(false);
      this.#toggleCart(false);
    }, { signal });

    // Aggiunte al carrello da qualsiasi punto del tema (pagina prodotto, quick add)
    document.addEventListener('shopify:cart:lines-update', async (event) => {
      let ok = true;
      try {
        if (event.promise) await event.promise;
      } catch {
        ok = false;
      }
      // Anche se l'aggiunta è fallita il carrello potrebbe essere cambiato: rileggilo sempre
      await this.refreshCart();
      if (ok && event.action === 'add') this.#toggleCart(true);
    }, { signal });

    // Pagina ripristinata col tasto indietro (bfcache): il carrello in memoria può essere vecchio
    window.addEventListener('pageshow', (e) => e.persisted && this.refreshCart(), { signal });

    window.NfrCart = { open: () => this.openCart(), refresh: () => this.refreshCart() };
    this.refreshCart();
  }

  disconnectedCallback() {
    this.#abort.abort();
    this.layer?.remove();
    this.layer = null;
  }

  #lock() {
    const anyOpen = this.$('.nfr-menu.is-open, .nfr-drawer.is-open') || !this.$('[data-search]').hidden;
    document.body.classList.toggle('nfr-locked', Boolean(anyOpen));
  }

  #toggleMenu(open) {
    const menu = this.$('[data-menu]');
    menu.classList.toggle('is-open', open);
    menu.inert = !open;
    this.$('[data-menu-overlay]').classList.toggle('is-open', open);
    this.$('[data-open-menu]').setAttribute('aria-expanded', String(open));
    if (open) this.$('[data-close-menu]').focus();
    this.#lock();
  }

  #toggleSearch(open) {
    const modal = this.$('[data-search]');
    if (modal.hidden === !open) return;
    modal.hidden = !open;
    if (open) {
      this.#renderRecent();
      setTimeout(() => this.$('[data-search-input]').focus(), 200);
    } else {
      this.$('[data-search-input]').value = '';
      this.#onSearchInput();
    }
    this.#lock();
  }

  #toggleCart(open) {
    const drawer = this.$('[data-cart]');
    if (drawer.classList.contains('is-open') === open) return;
    drawer.classList.toggle('is-open', open);
    drawer.inert = !open;
    this.$('[data-cart-overlay]').classList.toggle('is-open', open);
    if (open) this.$('[data-close-cart]').focus();
    this.#lock();
  }

  async openCart() {
    this.#toggleCart(true);
    await this.refreshCart();
  }

  // ── RICERCA ──
  #recent() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch {
      return [];
    }
  }

  #saveRecent(term) {
    const next = [term, ...this.#recent().filter((r) => r !== term)].slice(0, 6);
    try {
      localStorage.setItem(RECENT_KEY, JSON.stringify(next));
    } catch {}
  }

  #renderRecent() {
    const recent = this.#recent();
    const q = this.$('[data-search-input]').value.trim();
    this.$('[data-recent]').hidden = q.length >= 2 || recent.length === 0;
    const list = this.$('[data-recent-list]');
    list.innerHTML = recent
      .map((r) => `<button type="button" class="nfr-sm__chip" data-recent-term="${escapeHtml(r)}"><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg><span>${escapeHtml(r)}</span></button>`)
      .join('');
    list.querySelectorAll('[data-recent-term]').forEach((chip) =>
      chip.addEventListener('click', () => {
        this.$('[data-search-input]').value = chip.dataset.recentTerm;
        this.#onSearchInput();
      })
    );
  }

  #onSearchInput() {
    const q = this.$('[data-search-input]').value.trim();
    this.$('[data-search-clear]').hidden = !q;
    this.$('[data-suggestions]').hidden = q.length >= 2;
    this.#renderRecent();
    clearTimeout(this.#searchTimer);
    this.#searchController?.abort();
    const loading = this.$('[data-search-loading]');
    const empty = this.$('[data-search-empty]');
    const results = this.$('[data-results]');
    if (q.length < 2) {
      loading.hidden = true;
      empty.hidden = true;
      results.hidden = true;
      return;
    }
    loading.hidden = false;
    empty.hidden = true;
    results.hidden = true;
    this.#searchTimer = setTimeout(() => this.#search(q), 250);
  }

  async #search(q) {
    this.#searchController = new AbortController();
    try {
      const suggest = async (term, unavailable) => {
        const url = `${ROOT}search/suggest.json?q=${encodeURIComponent(term)}&resources[type]=product&resources[limit]=10&resources[options][unavailable_products]=${unavailable}`;
        const data = await fetch(url, { signal: this.#searchController.signal }).then((r) => r.json());
        return data.resources?.results?.products || [];
      };
      const category = SEARCH_CATEGORIES.find((c) => c.re.test(q));
      let products;
      if (category) {
        // Prima i prodotti della categoria (disponibili, poi esauriti), poi il resto
        const rank = (p) => (category.type.test(p.type || '') ? 0 : 2) + (p.available ? 0 : 1);
        products = (await suggest(category.term, 'show')).map((p, i) => [p, i]).sort((a, b) => rank(a[0]) - rank(b[0]) || a[1] - b[1]).map(([p]) => p);
      } else {
        products = await suggest(q, 'last');
      }
      this.$('[data-search-loading]').hidden = true;
      if (!products.length) {
        this.$('[data-search-empty-t]').textContent = `Nessun risultato per "${q}"`;
        this.$('[data-search-empty]').hidden = false;
        return;
      }
      this.$('[data-results-count]').textContent = `${products.length} ${products.length === 1 ? 'risultato' : 'risultati'}`;
      const list = this.$('[data-results-list]');
      list.innerHTML = products
        .map((p) => {
          const price = Math.round(parseFloat(p.price) * 100);
          const compare = Math.round(parseFloat(p.compare_at_price_max || 0) * 100);
          const img = p.featured_image?.url || p.image;
          return `<a class="nfr-sm__result" href="${escapeHtml(p.url)}">
            <div class="nfr-sm__result-img">${img ? `<img src="${escapeHtml(sizedImage(img, 120))}" alt="" width="56" height="56">` : ''}</div>
            <div class="nfr-sm__result-info"><div class="nfr-sm__result-name">${escapeHtml(p.title)}</div><div class="nfr-sm__result-vendor">${escapeHtml(p.vendor)}${p.available ? '' : ' · <strong>Esaurito</strong>'}</div></div>
            <div class="nfr-sm__result-price"><span class="nfr-sm__price-now${compare > price ? ' is-sale' : ''}">${this.fmt(price)}</span>${compare > price ? `<span class="nfr-sm__price-was">${this.fmt(compare)}</span>` : ''}</div>
          </a>`;
        })
        .join('');
      list.querySelectorAll('a').forEach((a) => a.addEventListener('click', () => this.#saveRecent(q)));
      this.$('[data-results]').hidden = false;
    } catch (err) {
      if (err.name !== 'AbortError') this.$('[data-search-loading]').hidden = true;
    }
  }

  // ── CARRELLO ──
  // Ogni lettura ha un numero: una risposta arrivata in ritardo (es. quella al caricamento
  // della pagina, su rete lenta) non sovrascrive mai un carrello più recente.
  async refreshCart() {
    const seq = ++this.#cartSeq;
    try {
      const cart = await fetch(`${ROOT}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' }).then((r) => r.json());
      if (seq !== this.#cartSeq) return;
      this.#cart = cart;
      await this.#loadCompare();
      if (seq !== this.#cartSeq) return;
      this.#renderCart();
    } catch (err) {
      console.error('Carrello NFR:', err);
    }
  }

  // Prezzo di listino per variante (non incluso in /cart.js) dalla vista templates/cart.nfr.liquid
  async #loadCompare() {
    const missing = this.#cart.items.some((i) => !(String(i.variant_id) in (this.compare || {})));
    if (!missing) return;
    try {
      const data = await fetch(`${ROOT}cart?view=nfr`).then((r) => r.json());
      this.compare = { ...(this.compare || {}), ...data.compare };
    } catch {}
  }

  #renderCart() {
    const cart = this.#cart;
    const count = cart.item_count;
    document.documentElement.classList.toggle('nfr-has-cart', count > 0);

    const badge = this.$('[data-cart-count]');
    badge.textContent = count;
    badge.hidden = count === 0;
    this.$('[data-cart-title-count]').textContent = count > 0 ? `(${count})` : '';

    // Barra promo: con un numero dispari di capi il prossimo chiude la coppia a -50%
    const promoBar = this.$('[data-promo-bar]');
    promoBar.hidden = !(count > 0 && count % 2 === 1);
    this.$('[data-promo-count]').textContent = `${count} ${count === 1 ? 'articolo' : 'articoli'}`;

    // Come funziona il 2° capo a -50% (sconto automatico: in ogni coppia il capo meno caro è a metà)
    const how = this.$('[data-promo-how]');
    if (how) {
      how.hidden = count === 0;
      how.innerHTML = count % 2 === 1
        ? '⚡ <strong>Aggiungi un altro capo qualsiasi</strong>: il meno caro dei due ti costa la metà e la spedizione diventa gratis (Italia e UE). Si applica da solo, senza codice.'
        : '✓ <strong>2° capo a -50% applicato</strong>: in ogni coppia di capi, il meno caro è a metà prezzo. Spedizione gratuita in Italia e UE.';
    }

    const empty = count === 0;
    this.$('[data-cart-empty]').hidden = !empty;
    this.$('[data-cart-foot]').hidden = empty;

    this.$('[data-cart-items]').innerHTML = cart.items
      .map((item, index) => {
        const lineDiscount = item.original_line_price - item.final_line_price;
        const half = item.price / 2;
        const discQty = lineDiscount > 0 ? Math.min(item.quantity, Math.round(lineDiscount / half)) : 0;
        const fullQty = item.quantity - discQty;
        const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="nfr-dr__variant">${escapeHtml(item.variant_title)}</div>` : '';
        const price = discQty > 0
          ? `<div class="nfr-dr__price-row">${fullQty > 0 ? `<span class="nfr-dr__price">${fullQty > 1 ? `${fullQty}× ` : ''}${this.fmt(item.price)}</span>` : ''}<span class="nfr-dr__price-promo"><span class="nfr-dr__price-strike">${this.fmt(item.price)}</span><span class="nfr-dr__price-half">${this.fmt(half)}</span><span class="nfr-dr__promo-badge">-50%</span></span></div>`
          : (this.compare?.[item.variant_id] || 0) > item.price
            ? `<div class="nfr-dr__price-row"><span class="nfr-dr__price is-sale">${this.fmt(item.price)}</span><span class="nfr-dr__price-strike">${this.fmt(this.compare[item.variant_id])}</span></div>`
            : `<div class="nfr-dr__price">${this.fmt(item.price)}</div>`;
        return `<div class="nfr-dr__item" data-line="${index + 1}" data-qty="${item.quantity}">
          <a class="nfr-dr__img" href="${escapeHtml(item.url)}">${item.image ? `<img src="${escapeHtml(sizedImage(item.image, 160))}" alt="" width="64" height="64">` : ''}</a>
          <div class="nfr-dr__info"><a class="nfr-dr__name" href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a>${variant}${price}</div>
          <div class="nfr-dr__right">
            <div class="nfr-dr__qty">
              <button type="button" data-change="-1" aria-label="${item.quantity <= 1 ? 'Rimuovi dal carrello' : 'Diminuisci quantità'}">−</button>
              <span>${item.quantity}</span>
              <button type="button" data-change="1" aria-label="Aumenta quantità">+</button>
            </div>
            <button type="button" class="nfr-dr__rm" data-remove aria-label="Rimuovi dal carrello"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg></button>
          </div>
        </div>`;
      })
      .join('');

    // Sconti: automatico "2° capo" e codici, ricavati dalle allocazioni reali di Shopify
    const totals = new Map();
    for (const item of cart.items) {
      for (const alloc of item.line_level_discount_allocations || []) {
        const title = alloc.discount_application.title || alloc.discount_application.key;
        totals.set(title, (totals.get(title) || 0) + alloc.amount);
      }
    }
    for (const app of cart.cart_level_discount_applications || []) {
      totals.set(app.title, (totals.get(app.title) || 0) + app.total_allocated_amount);
    }
    const totalDiscount = cart.original_total_price - cart.total_price;
    const breakdown = this.$('[data-breakdown]');
    breakdown.hidden = totalDiscount <= 0;
    breakdown.innerHTML = totalDiscount > 0
      ? `<div class="nfr-dr__row"><span>Subtotale</span><span>${this.fmt(cart.original_total_price)}</span></div>` +
        [...totals].filter(([, amount]) => amount > 0).map(([title, amount]) => `<div class="nfr-dr__row nfr-dr__row--promo"><span>${escapeHtml(title)}</span><span>-${this.fmt(amount)}</span></div>`).join('')
      : '';

    this.$('[data-cart-original]').textContent = totalDiscount > 0 ? this.fmt(cart.original_total_price) : '';
    this.$('[data-cart-total]').textContent = this.fmt(cart.total_price);
    this.$('[data-cart-total]').classList.toggle('is-sale', totalDiscount > 0);
    const saving = this.$('[data-cart-saving]');
    saving.hidden = totalDiscount <= 0;
    saving.textContent = `Risparmi ${this.fmt(totalDiscount)} su questo ordine`;

    this.#renderUpsell();

    // Codice sconto applicato
    const code = (cart.discount_codes || []).find((d) => d.applicable);
    this.$('[data-code-applied]').hidden = !code;
    this.$('[data-code-form]').hidden = Boolean(code);
    if (code) this.$('[data-code-applied-text]').textContent = code.code;
  }

  // Prodotti consigliati da Shopify per il primo articolo del carrello, esclusi quelli già presenti
  async #renderUpsell() {
    const box = this.$('[data-upsell]');
    const cart = this.#cart;
    if (!cart.items.length) {
      box.hidden = true;
      return;
    }
    const productId = cart.items[0].product_id;
    if (this.upsellFor !== productId) {
      this.upsellFor = productId;
      try {
        const data = await fetch(`${ROOT}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`).then((r) => r.json());
        let products = data.products || [];
        if (products.length < 3) {
          const related = await fetch(`${ROOT}recommendations/products.json?product_id=${productId}&limit=10`).then((r) => r.json());
          products = [...products, ...(related.products || [])];
        }
        this.upsellProducts = products;
      } catch {
        this.upsellProducts = [];
      }
    }
    const inCart = new Set(cart.items.map((i) => i.product_id));
    const products = (this.upsellProducts || []).filter((p) => p.available && !inCart.has(p.id)).slice(0, 8);
    box.hidden = products.length === 0;
    if (!products.length) return;
    const promo = cart.item_count % 2 === 1;
    this.$('[data-upsell-title]').innerHTML = promo ? 'Aggiungi il prossimo a <strong>-50%</strong>' : 'Completa il look';
    this.$('[data-upsell-strip]').innerHTML = products
      .map((p) => {
        const img = p.featured_image ? sizedImage(p.featured_image, 300) : '';
        const price = promo
          ? `<span class="nfr-up__now is-sale">${this.fmt(p.price / 2)}</span><span class="nfr-up__was">${this.fmt(p.price)}</span>`
          : `<span class="nfr-up__now${p.compare_at_price > p.price ? ' is-sale' : ''}">${this.fmt(p.price)}</span>${p.compare_at_price > p.price ? `<span class="nfr-up__was">${this.fmt(p.compare_at_price)}</span>` : ''}`;
        return `<a class="nfr-up" href="${escapeHtml(p.url)}">
          <span class="nfr-up__img">${img ? `<img src="${escapeHtml(img)}" alt="" width="120" height="120">` : ''}${promo ? '<span class="nfr-up__badge">-50%</span>' : ''}</span>
          <span class="nfr-up__name">${escapeHtml(p.title)}</span>
          <span class="nfr-up__price">${price}</span>
          <span class="nfr-up__cta">Scegli taglia</span>
        </a>`;
      })
      .join('');
  }

  async #onCartClick(event) {
    const button = event.target.closest('[data-change], [data-remove]');
    if (!button) return;
    const row = button.closest('[data-line]');
    const qty = button.hasAttribute('data-remove') ? 0 : Math.max(0, Number(row.dataset.qty) + Number(button.dataset.change));
    row.classList.add('is-busy');
    await this.#post('cart/change.js', { line: Number(row.dataset.line), quantity: qty });
  }

  async #applyCode(code) {
    const msg = this.$('[data-code-msg]');
    msg.hidden = true;
    const btn = this.$('[data-code-btn]');
    btn.textContent = '…';
    btn.disabled = true;
    await this.#post('cart/update.js', { discount: code });
    btn.textContent = 'Applica';
    if (code) {
      const applied = (this.#cart.discount_codes || []).find((d) => d.code.toUpperCase() === code.toUpperCase());
      if (!applied || !applied.applicable) {
        msg.textContent = applied ? 'Codice non applicabile a questo carrello' : 'Codice non valido';
        msg.hidden = false;
        if (applied) await this.#post('cart/update.js', { discount: '' });
      } else {
        this.$('[data-code-input]').value = '';
      }
    }
  }

  async #post(path, body) {
    try {
      const response = await fetch(`${ROOT}${path}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await response.json();
      if (data.items) {
        ++this.#cartSeq; // le letture ancora in corso sono più vecchie di questa risposta
        this.#cart = data;
        await this.#loadCompare();
        this.#renderCart();
      } else {
        await this.refreshCart();
      }
    } catch {
      await this.refreshCart();
    }
  }
}

if (!customElements.get('nfr-header')) customElements.define('nfr-header', NfrHeader);
